import type { Column, TableState } from './types'

function rawValue<T>(row: T, column: Column<T>): string | number {
  if (column.value) return column.value(row)
  const value = (row as Record<string, unknown>)[column.key]
  return typeof value === 'number' ? value : String(value ?? '')
}

/**
 * Search, filter, sort and paginate rows on the client.
 *
 * Deliberately a plain function rather than something baked into <DataTable>:
 * here there is no server, so a page calls this over its fixtures. In
 * roundhouse-rails the controller has already done all four, so the Inertia
 * page skips this entirely and hands server-prepared rows to the very same
 * <DataTable>. The component never learns which happened.
 */
export function applyTableState<T>(
  rows: readonly T[],
  columns: readonly Column<T>[],
  state: TableState,
): { rows: T[]; total: number } {
  let result = [...rows]

  const query = state.search.trim().toLowerCase()
  if (query) {
    result = result.filter((row) =>
      columns.some((column) => String(rawValue(row, column)).toLowerCase().includes(query)),
    )
  }

  for (const [key, selected] of Object.entries(state.filters)) {
    if (!selected) continue
    const column = columns.find((c) => c.key === key)
    if (!column) continue
    result = result.filter((row) => String(rawValue(row, column)) === selected)
  }

  if (state.sortKey) {
    const column = columns.find((c) => c.key === state.sortKey)
    if (column) {
      const direction = state.sortDir === 'asc' ? 1 : -1
      result.sort((a, b) => {
        const left = rawValue(a, column)
        const right = rawValue(b, column)
        if (typeof left === 'number' && typeof right === 'number') {
          return (left - right) * direction
        }
        return String(left).localeCompare(String(right), undefined, { numeric: true }) * direction
      })
    }
  }

  const total = result.length
  const start = (state.page - 1) * state.pageSize

  return { rows: result.slice(start, start + state.pageSize), total }
}
